import { tracked } from "@glimmer/tracking"
import {
  isUnifiedCardStack,
  readonlyLegacyCopy,
  readonlyUnifiedCopy,
} from "@shared/lib/card-stack/unified-migration"
import type { CardStack } from "@shared/models/card-stack"
import Patch from "@shared/models/patch-updater"
import type PatchCoordinator from "../services/patch-coordinator"

/**
 * Debugging aid: renders one lesson in the other editor. Off until `CCDEV_MODE()` is called from
 * the console, like `CCIMPERSONATE_PLANBOOK` — the mode lasts for the page load, no longer.
 *
 * The schemaVersion flip rides `applyForeignPatch`, which updates both stores and persists
 * nothing, so a reload restores whatever the server holds. Editing a flipped lesson writes
 * patches shaped for the editor on screen — fine for a debugging session, not for real data.
 */
class DevLessonEditorSwitch {
  @tracked isEnabled = false
  @tracked private forcedLegacy: ReadonlySet<string> = new Set()

  /**
   * A unified planbook picks the React island from the planbook flag alone, before any lesson is
   * loaded, so `lesson-island-bridge` needs this to hand the day back to Ember. Keyed by course
   * and date because that is all the bridge knows — which means moving a lesson off a switched
   * day (bump, paste) leaves the key pointing at whatever lands there next. Reload to clear it.
   */
  isForcedLegacy(courseId: string, dateString: string): boolean {
    return this.forcedLegacy.has(`${courseId}|${dateString}`)
  }

  toggle(
    patchCoordinator: PatchCoordinator,
    lesson: CardStack,
    courseId: string,
    dateString: string
  ): void {
    const toLegacy = isUnifiedCardStack(lesson)
    const { schemaVersion, proseMirror, extracted } = toLegacy
      ? readonlyLegacyCopy(lesson).attributes
      : readonlyUnifiedCopy(lesson).attributes

    patchCoordinator.applyForeignPatch(
      Patch.create("card-stack", lesson.id)
        .set("attributes.schemaVersion", schemaVersion)
        .set("attributes.proseMirror", proseMirror)
        .set("attributes.extracted", extracted).patch
    )

    const key = `${courseId}|${dateString}`
    const forcedLegacy = new Set(this.forcedLegacy)
    if (toLegacy) forcedLegacy.add(key)
    else forcedLegacy.delete(key)
    this.forcedLegacy = forcedLegacy
  }
}

export const devLessonEditorSwitch = new DevLessonEditorSwitch()

declare global {
  interface Window {
    CCDEV_MODE?: (on?: boolean) => void
  }
}

if (typeof window !== "undefined") {
  window.CCDEV_MODE = (on = true) => {
    devLessonEditorSwitch.isEnabled = on
    console.log(
      on
        ? "Dev mode on. The lesson menu can switch a lesson's editor; edits made to a switched lesson still save."
        : "Dev mode off."
    )
  }
}
